import { TableScroll } from '@web/components/table-scroll.tsx';
import type { ReactNode } from 'react';
import { formatCents } from '@shared/calculations/pricing.ts';
import { classes } from '../../components/ui.tsx';

/**
 * The formatting and small presentational pieces the portal's screens share.
 *
 * Kept in one file rather than repeated per page for the usual reason, and one
 * specific one: money on these screens is a partner's own earnings, and a page
 * that rounded or grouped it differently from the page next to it would read
 * as a discrepancy rather than as a style choice.
 */

export function money(cents: number, currency = 'USD'): string {
  return formatCents(cents, currency);
}

export function shortDate(value: string | null): string {
  if (!value) return '—';
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleDateString() : '—';
}

/** "2026-08" as a person reads it, with the raw value as the fallback. */
export function periodLabel(period: string): string {
  const parsed = Date.parse(`${period}-01T00:00:00Z`);
  if (!Number.isFinite(parsed)) return period;
  return new Date(parsed).toLocaleDateString(undefined, { year: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** The last `count` commission periods, most recent first. */
export function recentPeriods(count: number): string[] {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const month = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - index, 1));
    return month.toISOString().slice(0, 7);
  });
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="card">
      <p className="text-sm text-[color:var(--color-muted)]">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      {hint ? <p className="mt-1 text-sm text-[color:var(--color-muted)]">{hint}</p> : null}
    </div>
  );
}

/**
 * A neutral badge for the many small enumerations these screens show — a
 * payout status, a claim decision, a billing state.
 *
 * Color only reinforces the word, never replaces it (spec §27): the label is
 * always readable on its own.
 */
export function Pill({ value, tone = 'neutral' }: { value: string; tone?: 'neutral' | 'good' | 'warn' | 'bad' }) {
  const tones = {
    neutral: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
    good: 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
    warn: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
    bad: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
  } as const;
  return <span className={classes('badge', tones[tone])}>{value.replace(/_/g, ' ')}</span>;
}

export function TableShell({ label, head, children }: { label: string; head: ReactNode; children: ReactNode }) {
  return (
    <TableScroll aria-label={label} className="card overflow-x-auto p-0">
      {/* `w-full` alone pins the table to the wrapper, so the wrapper never
          has anything to scroll: on a phone the columns squeeze until cells
          break mid-word. `min-w-max` is a no-op while the content fits and
          hands the wrapper a scroll when it does not. */}
      <table className="data-table w-full min-w-max text-left text-sm">
        <thead className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">{head}</thead>
        <tbody>{children}</tbody>
      </table>
    </TableScroll>
  );
}
